import React, { useEffect, useState } from 'react';
import {
    X, CheckCircle, XCircle, AlertCircle, MessageSquare,
    Send, ThumbsUp, ThumbsDown, Check, Reply, Trash2,
    User, Clock, Calendar
} from 'lucide-react';
import { useCollaborationStore } from '../../../store/collaborationStore';
import { ReviewRequest, Comment } from '../../../services/collaboration.service';
import { useAuthStore } from '../../../store/authStore';

interface ReviewDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    review: ReviewRequest | null;
}

const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({ isOpen, onClose, review }) => {
    const { user } = useAuthStore();
    const {
        comments,
        fetchComments,
        createComment,
        resolveComment,
        addReaction,
        deleteComment,
        makeDecision,
        fetchReviews,
    } = useCollaborationStore();

    const [newComment, setNewComment] = useState('');
    const [replyTo, setReplyTo] = useState<string | null>(null);
    const [replyContent, setReplyContent] = useState('');
    const [decisionComment, setDecisionComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && review) {
            fetchComments(review.id);
        }
    }, [isOpen, review, fetchComments]);

    const handleAddComment = async () => {
        if (!newComment.trim() || !review) return;
        setIsSubmitting(true);
        try {
            await createComment(review.id, newComment.trim());
            setNewComment('');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReply = async (parentId: string) => {
        if (!replyContent.trim() || !review) return;
        setIsSubmitting(true);
        try {
            await createComment(review.id, replyContent.trim(), parentId);
            setReplyTo(null);
            setReplyContent('');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMakeDecision = async (action: 'approved' | 'rejected' | 'changes_requested') => {
        if (!review) return;
        setIsSubmitting(true);
        try {
            const decision: { action: 'approved' | 'rejected' | 'changes_requested'; comment?: string } = { action };
            if (decisionComment.trim()) {
                decision.comment = decisionComment.trim();
            }
            await makeDecision(review.id, decision);
            await fetchReviews();
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const configs: Record<string, { bg: string; text: string; label: string }> = {
            pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', label: 'Pending' },
            in_review: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'In Review' },
            changes_requested: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', label: 'Changes Requested' },
            approved: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Approved' },
            rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Rejected' },
        };
        const config = configs[status] || configs.pending;
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    // Render a single comment with its children
    const CommentItem: React.FC<{ comment: Comment; depth?: number }> = ({ comment, depth = 0 }) => {
        const likeCount = comment.reactions.filter(r => r.type === 'like').length;
        const dislikeCount = comment.reactions.filter(r => r.type === 'dislike').length;
        const hasLiked = comment.reactions.some(r => r.userId === user?.id && r.type === 'like');
        const hasDisliked = comment.reactions.some(r => r.userId === user?.id && r.type === 'dislike');

        return (
            <div className={`${depth > 0 ? 'ml-8 pl-4 border-l-2 border-gray-200 dark:border-gray-600' : ''}`}>
                <div className={`p-4 rounded-xl ${comment.resolved ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-700/50'} mb-3`}>
                    {/* Comment Header */}
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-indigo-400 flex items-center justify-center text-white text-sm font-medium">
                                {comment.authorId.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <span className="font-medium text-gray-900 dark:text-white text-sm">
                                    User {comment.authorId.slice(-4)}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                    {new Date(comment.createdAt).toLocaleString()}
                                    {comment.isEdited && ' (edited)'}
                                </span>
                            </div>
                        </div>
                        {comment.resolved && (
                            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                <Check className="w-3 h-3" />
                                Resolved
                            </span>
                        )}
                    </div>

                    {/* Comment Content */}
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-3 whitespace-pre-wrap">
                        {comment.content}
                    </p>

                    {/* Comment Actions */}
                    <div className="flex items-center gap-4 text-sm">
                        <button
                            onClick={() => addReaction(comment.id, 'like')}
                            className={`flex items-center gap-1 ${hasLiked ? 'text-primary-600' : 'text-gray-500 dark:text-gray-400'} hover:text-primary-600 transition-colors`}
                        >
                            <ThumbsUp className="w-4 h-4" />
                            {likeCount > 0 && likeCount}
                        </button>
                        <button
                            onClick={() => addReaction(comment.id, 'dislike')}
                            className={`flex items-center gap-1 ${hasDisliked ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'} hover:text-red-600 transition-colors`}
                        >
                            <ThumbsDown className="w-4 h-4" />
                            {dislikeCount > 0 && dislikeCount}
                        </button>
                        <button
                            onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                            className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-primary-600 transition-colors"
                        >
                            <Reply className="w-4 h-4" />
                            Reply
                        </button>
                        <button
                            onClick={() => resolveComment(comment.id)}
                            className={`flex items-center gap-1 ${comment.resolved ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'} hover:text-green-600 transition-colors`}
                        >
                            <Check className="w-4 h-4" />
                            {comment.resolved ? 'Unresolve' : 'Resolve'}
                        </button>
                        {comment.authorId === user?.id && (
                            <button
                                onClick={() => deleteComment(comment.id)}
                                className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-red-600 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Reply Input */}
                    {replyTo === comment.id && (
                        <div className="mt-3 flex gap-2">
                            <input
                                type="text"
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                placeholder="Write a reply..."
                                className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleReply(comment.id);
                                    }
                                }}
                            />
                            <button
                                onClick={() => handleReply(comment.id)}
                                disabled={isSubmitting || !replyContent.trim()}
                                className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Nested Replies */}
                {comment.children && comment.children.length > 0 && (
                    <div className="mt-2">
                        {comment.children.map((child) => (
                            <CommentItem key={child.id} comment={child} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen || !review) return null;

    const canMakeDecision = ['pending', 'in_review', 'changes_requested'].includes(review.status);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-3xl">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {review.title}
                                </h3>
                                {getStatusBadge(review.status)}
                            </div>
                            {review.description && (
                                <p className="text-gray-600 dark:text-gray-400">
                                    {review.description}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center gap-6 mb-6 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            <span>Created by: {review.createdBy.slice(-6)}</span>
                        </div>
                        {review.assignedTo && (
                            <div className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                <span>Assigned to: {review.assignedTo.slice(-6)}</span>
                            </div>
                        )}
                        {review.dueDate && (
                            <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>Due: {new Date(review.dueDate).toLocaleDateString()}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                        </div>
                    </div>

                    {/* Decision Panel (if can make decision) */}
                    {canMakeDecision && (
                        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Review Actions</h4>
                            <textarea
                                value={decisionComment}
                                onChange={(e) => setDecisionComment(e.target.value)}
                                placeholder="Add a comment with your decision (optional)..."
                                rows={2}
                                className="w-full mb-3 px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            />
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => handleMakeDecision('approved')}
                                    disabled={isSubmitting}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    Approve
                                </button>
                                <button
                                    onClick={() => handleMakeDecision('changes_requested')}
                                    disabled={isSubmitting}
                                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50 transition-colors"
                                >
                                    <AlertCircle className="w-4 h-4" />
                                    Request Changes
                                </button>
                                <button
                                    onClick={() => handleMakeDecision('rejected')}
                                    disabled={isSubmitting}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
                                >
                                    <XCircle className="w-4 h-4" />
                                    Reject
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Comments Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <MessageSquare className="w-5 h-5" />
                            Comments ({comments.length})
                        </h4>

                        {/* New Comment Input */}
                        <div className="mb-6">
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-indigo-400 flex items-center justify-center text-white font-medium">
                                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div className="flex-1">
                                    <textarea
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        placeholder="Add a comment... Use @mention to tag team members"
                                        rows={3}
                                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                                    />
                                    <div className="flex justify-end mt-2">
                                        <button
                                            onClick={handleAddComment}
                                            disabled={isSubmitting || !newComment.trim()}
                                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
                                        >
                                            <Send className="w-4 h-4" />
                                            Comment
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Comments List */}
                        <div className="space-y-4 max-h-[400px] overflow-y-auto">
                            {comments.length === 0 ? (
                                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                                    No comments yet. Be the first to add feedback!
                                </p>
                            ) : (
                                comments.map((comment) => (
                                    <CommentItem key={comment.id} comment={comment} />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewDetailModal;
